import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { agentEmail, agentLogin } from "../../utils/api";

const buttonClass = "inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50";
const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : null;
};

const AgentEmailSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [proofSent, setProofSent] = useState(false);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const inFlight = useRef(false);
  const errorRef = useRef(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await agentEmail.get();
      setSettings(response.data);
      setNeedsSignIn(false);
    } catch (requestError) {
      setLoadError("We couldn’t load your email settings. Try again, or sign in again if your session has ended.");
      if (requestError.response?.status === 401) setNeedsSignIn(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => {
    if (error || loadError) errorRef.current?.focus();
  }, [error, loadError]);

  const runAction = async (action, operation) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(action);
    setError("");
    setNotice("");
    try {
      await operation();
    } catch (requestError) {
      const status = requestError.response?.status;
      if (status === 401) setNeedsSignIn(true);
      setError(status === 403
        ? "This request couldn’t be verified. Reload Email settings in the HelloPC app and try again."
        : requestError.response?.data?.message || "We couldn’t verify the result. Check your current email and pending request before trying again.");
      // A fresh read can reveal expired proof or an already-completed request;
      // it never sends another email or retries a mutation.
      if (status !== 401) await loadSettings();
    } finally {
      inFlight.current = false;
      setBusy("");
    }
  };

  const requestChange = (event) => {
    event.preventDefault();
    if (inFlight.current || !settings?.canRequestChange || settings.pending || needsSignIn || loadError) return;
    const nextEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail) || nextEmail.length > 254) {
      setError("Enter a valid new email address.");
      return;
    }
    if (nextEmail === settings.email?.trim().toLowerCase()) {
      setError("Enter an email address different from your current address.");
      return;
    }
    runAction("request", async () => {
      const response = await agentEmail.requestChange(nextEmail);
      setSettings((current) => ({ ...current, pending: response.data.pending || null }));
      setEmail("");
      setNotice(response.data.message || "Check the new address for a verification email. Your current email is unchanged.");
    });
  };

  const requestProof = () => runAction("proof", async () => {
    await agentLogin.request(settings.email, "/agent/email");
    setProofSent(true);
    setNotice("If this address is connected to an active account, a sign-in email is on its way. Open it in this browser and choose Confirm and sign in to verify access, then return here.");
  });

  const cancelChange = () => runAction("cancel", async () => {
    const requestId = settings.pending.requestId;
    await agentEmail.cancelChange(requestId);
    setSettings((current) => current.pending?.requestId === requestId ? { ...current, pending: null } : current);
    setNotice("Cancellation processed. Check your current email and any pending request below.");
    await loadSettings();
  });

  const verification = settings?.emailVerification;
  const verificationStatus = verification?.status === "verified" && verification.email === settings?.email
    ? "Verified"
    : verification?.status === "unverified" ? "Unverified" : "Unknown";
  const verifiedAt = verificationStatus === "Verified" ? formatDate(verification?.verifiedAt) : null;
  const pendingExpiresAt = formatDate(settings?.pending?.expiresAt);
  const disabled = Boolean(busy) || loading || needsSignIn || Boolean(loadError);

  return (
    <main id="agent-email-settings" className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 px-4 py-8">
      <section id="agent-email-settings-card" className="mx-auto max-w-xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm" aria-labelledby="agent-email-heading" aria-busy={loading || Boolean(busy)}>
        <Link to="/agent/dashboard" className="mb-6 inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" /> Back to dashboard
        </Link>
        <h1 id="agent-email-heading" className="flex items-center text-2xl font-bold text-gray-900"><Mail className="mr-2 h-6 w-6" aria-hidden="true" />Email settings</h1>
        <p className="mt-2 text-sm text-gray-600">Your current address stays active until you confirm the new address. After confirmation, other devices are signed out and this browser stays signed in with a new session.</p>
        {(loadError || error) && <p id="agent-email-error" ref={errorRef} tabIndex={-1} role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error || loadError}</p>}
        {notice && <p role="status" className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">{notice}</p>}
        {loading && <p role="status" className="mt-4 flex items-center text-sm text-gray-600"><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />Loading email settings…</p>}
        {needsSignIn && <a href="/agent/login?next=%2Fagent%2Femail" className="mt-4 inline-block text-sm text-blue-600 underline">Sign in again to manage your email</a>}
        {loadError && !loading && <button type="button" onClick={loadSettings} disabled={Boolean(busy)} className={`${buttonClass} mt-4`}>Try loading again</button>}
        {settings && <>
          <section className="mt-6 rounded-lg bg-gray-50 p-4" aria-labelledby="agent-current-email-heading">
            <h2 id="agent-current-email-heading" className="text-sm font-semibold text-gray-900">Current email</h2>
            <p className="mt-1 break-words text-gray-900">{settings.email}</p>
            <p className="mt-1 text-sm text-gray-600">Verification: {verificationStatus}{verifiedAt ? ` · Verified ${verifiedAt}` : ""}</p>
            {verificationStatus !== "Verified" && <p className="mt-2 text-sm text-gray-600">This address has not been confirmed by a recent email-link sign-in. A coach entering an address does not verify ownership.</p>}
          </section>
          {settings.pending && <section className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4" aria-labelledby="agent-pending-email-heading">
            <h2 id="agent-pending-email-heading" className="font-semibold text-gray-900">Awaiting confirmation</h2>
            <p className="mt-1 break-words text-sm text-gray-900">{settings.pending.email}</p>
            {pendingExpiresAt && <p className="mt-1 text-sm text-gray-600">Expires {pendingExpiresAt}</p>}
            <p className="mt-2 text-sm text-gray-600">Open the verification email in this same signed-in browser and choose Confirm email change. To use a different address or request a fresh link, cancel this request first.</p>
            <button type="button" disabled={disabled} onClick={cancelChange} className="mt-3 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50">{busy === "cancel" ? "Canceling…" : "Cancel pending change"}</button>
          </section>}
          {!settings.canRequestChange && <section className="mt-6" aria-labelledby="agent-email-proof-heading">
            <h2 id="agent-email-proof-heading" className="font-semibold text-gray-900">Verify access to your current email</h2>
            <p className="mt-2 text-sm text-gray-600">Before requesting a change, sign in with a fresh link sent to {settings.email}. If you can’t access that inbox, contact your coach for help.</p>
            <button type="button" onClick={requestProof} disabled={disabled || proofSent || !settings.email} className={`${buttonClass} mt-3`}>{busy === "proof" ? "Requesting sign-in email…" : proofSent ? "Sign-in email requested" : "Send a fresh sign-in email"}</button>
          </section>}
          {settings.canRequestChange && !settings.pending && <form aria-label="Change email address" onSubmit={requestChange} className="mt-6" noValidate>
            <label htmlFor="agent-new-email" className="block text-sm font-medium text-gray-900">New email address</label>
            <input id="agent-new-email" type="email" autoComplete="email" maxLength={254} required value={email} onChange={(event) => { setEmail(event.target.value); setError(""); }} disabled={disabled} aria-describedby="agent-email-form-help" aria-invalid={Boolean(error)} className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50" />
            <p id="agent-email-form-help" className="mt-2 text-sm text-gray-600">We’ll send a one-time verification link to this address. Opening the email alone won’t change your address.</p>
            <button type="submit" disabled={disabled} className={`${buttonClass} mt-4`}>{busy === "request" ? "Requesting verification…" : "Send verification email"}</button>
          </form>}
        </>}
      </section>
    </main>
  );
};

export default AgentEmailSettings;
